import { Children, ReactNode } from "react";
import { LogisticsRequestQuote } from "../../../types/PartnerCompanyDetails";
import { BoxIcon, MapPinIcon } from "../../Icons";
import { getRFQStatusStyles } from "../CustomerRFQsDetails/RFQCard";

function Root({
  quote,
  onClick,
  children,
}: {
  quote: LogisticsRequestQuote;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <tr
      onClick={onClick}
      className="cursor-pointer hover:bg-gray-50 transition-colors"
    >
      {children}
    </tr>
  );
}

function Route({
  from,
  to,
  freightType,
  weight,
}: {
  from?: string;
  to?: string;
  freightType?: string;
  weight?: string | number;
}) {
  return (
    <td className="px-4 py-4">
      <div className="flex items-center gap-2">
        <MapPinIcon className="size-5 text-blue-400 mt-0.5 shrink-0" />

        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-gray-900">
            {from} → {to}
          </p>

          <div className="mt-0.5 flex items-center gap-2 text-[11px] text-gray-500">
            <span className="uppercase tracking-wide">
              {freightType || "—"}
            </span>
            <span className="text-gray-400">•</span>
            <span>{weight ? `${weight} kg` : "—"}</span>
          </div>
        </div>
      </div>
    </td>
  );
}

function TransactionId({ id }: { id?: string }) {
  return <td className="px-4 py-4 text-gray-600 truncate">{id || "—"}</td>;
}

function DateCell({ date }: { date?: string }) {
  return (
    <td className="px-4 py-4 text-gray-500 text-xs">
      {date
        ? new Date(date).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })
        : "—"}
    </td>
  );
}

function TaskCell() {
  return <td className="px-4 py-4 text-center text-gray-700 font-medium">0</td>;
}

function StatusCell({ status }: { status: LogisticsRequestQuote["status"] }) {
  return (
    <td className="px-4 py-4">
      <span
        className={`text-xs px-2 py-1 rounded-full font-medium capitalize ${getRFQStatusStyles(status).className}`}
      >
        {getRFQStatusStyles(status).label}
      </span>
    </td>
  );
}

function ProcurementRequest({
  location,
  description,
}: {
  location?: string;
  description?: string;
}) {
  return (
    <td className="px-4 py-4">
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-gray-700">
          <MapPinIcon className="size-5 text-blue-400 mt-0.5 shrink-0" />

          <h3 className="text-sm font-semibold">{location}</h3>
        </div>

        <div className="flex items-center gap-2 text-sm text-gray-600">
          <BoxIcon className="size-3 text-gray-500 shrink-0" />

          <p>{description}</p>
        </div>
      </div>
    </td>
  );
}

export const QuoteRow = Object.assign(Root, {
  Route,
  TransactionId,
  Date: DateCell,
  Task: TaskCell,
  Status: StatusCell,
  ProcurementRequest,
});
